import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Bot, KeyRound, RefreshCw, ShieldAlert } from 'lucide-react';

import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import {
  applyRuntimePullback,
  createRuntimeOutputJob,
  createRuntimeSession,
  executeRuntimeBrain,
  getRuntimeFeatureFlags,
  getRuntimeOutputJob,
  getRuntimeOverview,
  getRuntimeTelemetry,
  type BrainResult,
  type OutputJob,
  type RuntimeOverview,
  type ShellSession,
} from '../services/api';

type SurfaceState = 'dormant' | 'attentive' | 'activated' | 'committed';
type AccessMode = 'substrate' | 'byok';
type RouteChoice = 'auto' | 'guided' | 'recall' | 'blended' | 'assist';

type TelemetryEntry = {
  ts: string;
  primary_attempted: string;
  fallback_triggered: boolean;
  reason: string;
  user_visible_impact: string;
};

const ACCESS_STORAGE_KEY = 'synaptic-clone.access';

const routeChoices: Array<{ id: RouteChoice; label: string; apiValue: 'auto' | 'ouroboros' | 'simd' | 'dual' | 'llm' }> = [
  { id: 'auto', label: 'automatic', apiValue: 'auto' },
  { id: 'guided', label: 'guided', apiValue: 'ouroboros' },
  { id: 'recall', label: 'recall', apiValue: 'simd' },
  { id: 'blended', label: 'blended', apiValue: 'dual' },
  { id: 'assist', label: 'assist', apiValue: 'llm' },
];

export default function SovereignShipment() {
  const [overview, setOverview] = useState<RuntimeOverview | null>(null);
  const [session, setSession] = useState<ShellSession | null>(null);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
  const [telemetry, setTelemetry] = useState<TelemetryEntry[]>([]);
  const [brainResult, setBrainResult] = useState<BrainResult | null>(null);
  const [outputJob, setOutputJob] = useState<OutputJob | null>(null);
  const [loading, setLoading] = useState(false);
  const [intent, setIntent] = useState('orchestrate');
  const [goal, setGoal] = useState('Prepare a response surface that can continue without interruption.');
  const [identityRef, setIdentityRef] = useState('continuity/default');
  const [artifactType, setArtifactType] = useState('response');
  const [routeChoice, setRouteChoice] = useState<RouteChoice>('auto');
  const [accessMode, setAccessMode] = useState<AccessMode>('substrate');
  const [provider, setProvider] = useState('OpenAI-compatible');
  const [apiKey, setApiKey] = useState('');
  const [modelName, setModelName] = useState('substrate-free');
  const [savedAccess, setSavedAccess] = useState(false);

  const fallbackCount = useMemo(
    () => telemetry.filter((entry) => entry.fallback_triggered).length,
    [telemetry]
  );

  const surfaceState = useMemo<SurfaceState>(() => {
    if (!session) return 'dormant';
    if (outputJob?.status === 'complete') return 'committed';
    if (loading || outputJob?.status === 'queued' || outputJob?.status === 'running') return 'activated';
    if (brainResult || fallbackCount > 0) return 'attentive';
    return 'attentive';
  }, [brainResult, fallbackCount, loading, outputJob, session]);

  useEffect(() => {
    const saved = window.localStorage.getItem(ACCESS_STORAGE_KEY);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved) as {
        accessMode?: AccessMode;
        provider?: string;
        apiKey?: string;
        modelName?: string;
      };
      setAccessMode(parsed.accessMode || 'substrate');
      setProvider(parsed.provider || 'OpenAI-compatible');
      setApiKey(parsed.apiKey || '');
      setModelName(parsed.modelName || 'substrate-free');
    } catch {
      // Ignore malformed local preferences.
    }
  }, []);

  async function refresh() {
    const [overviewRes, flagsRes, telemetryRes] = await Promise.all([
      getRuntimeOverview(),
      getRuntimeFeatureFlags(),
      getRuntimeTelemetry(),
    ]);

    if (overviewRes.success && overviewRes.data) setOverview(overviewRes.data);
    if (flagsRes.success && flagsRes.data) setFeatureFlags(flagsRes.data);
    if (telemetryRes.success && telemetryRes.data) setTelemetry(telemetryRes.data);
  }

  useEffect(() => {
    void (async () => {
      const sessionRes = await createRuntimeSession({
        user: 'clone-operator',
        shell_type: 'web',
        identity_ref: 'continuity/default',
      });
      if (sessionRes.success && sessionRes.data) {
        setSession(sessionRes.data);
      }
      await refresh();
    })();
  }, []);

  useEffect(() => {
    if (!outputJob || outputJob.status === 'complete' || outputJob.status === 'failed') {
      return;
    }

    const timer = window.setInterval(async () => {
      const result = await getRuntimeOutputJob(outputJob.id);
      if (result.success && result.data) {
        setOutputJob(result.data);
        if (result.data.status === 'complete' || result.data.status === 'failed') {
          window.clearInterval(timer);
          await refresh();
        }
      }
    }, 700);

    return () => window.clearInterval(timer);
  }, [outputJob]);

  async function handleExecute() {
    setLoading(true);
    try {
      const selectedRoute = routeChoices.find((choice) => choice.id === routeChoice)?.apiValue || 'auto';
      const result = await executeRuntimeBrain({
        intent,
        goal,
        identity_ref: identityRef || null,
        artifact_type: artifactType,
        route_policy: selectedRoute,
        session_id: session?.id || null,
      });
      if (result.success && result.data) {
        setBrainResult(result.data);
      }
      await refresh();
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateOutput() {
    setLoading(true);
    try {
      const result = await createRuntimeOutputJob({
        identity_ref: identityRef || null,
        artifact_type: artifactType,
        prompt: goal,
        preferred_backend: featureFlags.axiomPrimary ? 'axiom' : 'diffusion',
      });
      if (result.success && result.data) {
        setOutputJob(result.data);
      }
      await refresh();
    } finally {
      setLoading(false);
    }
  }

  async function handlePullback(target: 'arabic' | 'ouroboros' | 'simd' | 'axiom') {
    await applyRuntimePullback(target);
    await refresh();
  }

  function saveAccess() {
    window.localStorage.setItem(
      ACCESS_STORAGE_KEY,
      JSON.stringify({ accessMode, provider, apiKey, modelName })
    );
    setSavedAccess(true);
    window.setTimeout(() => setSavedAccess(false), 1200);
  }

  const outputStateLabel = outputJob
    ? outputJob.status === 'complete'
      ? '(delivery committed)'
      : outputJob.status === 'running'
        ? '(delivery resolving)'
        : outputJob.status === 'failed'
          ? '(delivery held)'
          : '(delivery queued)'
    : '(awaiting delivery selection)';

  return (
    <section id="session-field" className="epistemic-shell py-24 px-6 lg:px-10">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-3xl">
            <p className="epistemic-system-label mb-3">{stateMicrocopy(surfaceState)}</p>
            <h2 className="text-3xl lg:text-5xl font-semibold text-white mb-4">
              A surface that waits, confirms, and stays consistent.
            </h2>
            <p className="text-sm lg:text-base text-white/65">
              Gold appears only when the surface is earned. Internals stay hidden. The same access
              and delivery language remains stable even when the path underneath changes.
            </p>
          </div>
          <Button variant="outline" onClick={() => void refresh()} className="border-white/10 text-white/70 hover:text-synapse-gold">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh field
          </Button>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.25fr_0.9fr]">
          <Card className="epistemic-panel overflow-hidden">
            <CardContent className="p-6 lg:p-8">
              <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr] items-start">
                <div className="space-y-6">
                  <div className={`epistemic-field state-${surfaceState}`}>
                    <EpistemicTraceCanvas state={surfaceState} />
                    <div className="relative z-10 flex flex-wrap gap-2">
                      <Badge variant="outline" className="epistemic-badge">
                        {surfaceState}
                      </Badge>
                      <Badge variant="outline" className="epistemic-badge">
                        {outputStateLabel}
                      </Badge>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-4">
                    {stateCards(surfaceState).map((item) => (
                      <div key={item.title} className={`epistemic-state-card ${item.active ? 'is-active' : ''}`}>
                        <div className="epistemic-node-label">{item.title}</div>
                        <p className="epistemic-status-copy">{item.copy}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div id="access-controls" className="space-y-4">
                  <div>
                    <p className="epistemic-system-label">(model access)</p>
                    <h3 className="text-xl font-semibold text-white mt-2">Bring your key or stay on substrate.</h3>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <ModeButton active={accessMode === 'substrate'} onClick={() => setAccessMode('substrate')}>
                      substrate free
                    </ModeButton>
                    <ModeButton active={accessMode === 'byok'} onClick={() => setAccessMode('byok')}>
                      bring your key
                    </ModeButton>
                  </div>

                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label className="epistemic-node-label">Provider</Label>
                      <Input value={provider} onChange={(event) => setProvider(event.target.value)} className="epistemic-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="epistemic-node-label">Model</Label>
                      <Input value={modelName} onChange={(event) => setModelName(event.target.value)} className="epistemic-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="epistemic-node-label">Access key</Label>
                      <Input
                        type="password"
                        value={apiKey}
                        onChange={(event) => setApiKey(event.target.value)}
                        placeholder={accessMode === 'substrate' ? '(optional)' : '(required for your provider)'}
                        className="epistemic-input"
                      />
                    </div>
                    <div className="rounded-2xl border border-white/8 bg-black/20 p-4">
                      <p className="epistemic-status-copy">
                        (local acceleration may be installed for interface responsiveness while delivery stays isolated)
                      </p>
                    </div>
                    <Button onClick={saveAccess} className="bg-synapse-gold text-synapse-black hover:bg-synapse-amber">
                      <KeyRound className="w-4 h-4 mr-2" />
                      {savedAccess ? 'saved' : 'save access'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="epistemic-panel">
            <CardHeader>
              <CardTitle className="text-white text-2xl font-semibold">Choose a path without seeing the machinery.</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Intent" value={intent} onChange={setIntent} placeholder="orchestrate" />
                <Field label="Identity" value={identityRef} onChange={setIdentityRef} placeholder="continuity/default" />
                <Field label="Delivery" value={artifactType} onChange={setArtifactType} placeholder="response" />
                <div className="space-y-2">
                  <Label className="epistemic-node-label">Selection mode</Label>
                  <div className="flex flex-wrap gap-2">
                    {routeChoices.map((choice) => (
                      <ModeButton key={choice.id} active={routeChoice === choice.id} onClick={() => setRouteChoice(choice.id)}>
                        {choice.label}
                      </ModeButton>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="epistemic-node-label">Prompt</Label>
                <Textarea
                  value={goal}
                  onChange={(event) => setGoal(event.target.value)}
                  rows={4}
                  className="epistemic-input min-h-[120px]"
                  placeholder="(awaiting intent selection)"
                />
              </div>

              <div className="flex gap-3 flex-wrap">
                <Button onClick={() => void handleExecute()} disabled={loading} className="bg-synapse-gold text-synapse-black hover:bg-synapse-amber">
                  proceed
                </Button>
                <Button variant="outline" onClick={() => void handleCreateOutput()} disabled={loading} className="border-white/10 text-white/70 hover:text-synapse-gold">
                  <Bot className="w-4 h-4 mr-2" />
                  prepare delivery
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <Card className="epistemic-panel">
            <CardHeader>
              <CardTitle className="text-white">Field response</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge variant="outline" className="epistemic-badge">
                  {surfaceState}
                </Badge>
                <Badge variant="outline" className="epistemic-badge">
                  {brainResult?.fallback_used ? '(assist engaged)' : '(native path held)'}
                </Badge>
                <Badge variant="outline" className="epistemic-badge">
                  {translateMeaningMode(brainResult)}
                </Badge>
              </div>
              <ul className="space-y-3 text-sm text-white/68">
                {responseSteps(brainResult, outputJob).map((step) => (
                  <li key={step} className="flex items-start gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-synapse-gold/70" />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
              <Separator className="bg-white/8" />
              <div className="grid gap-3 md:grid-cols-3">
                <MetricCard label="sessions" value={overview?.sessions ?? '—'} />
                <MetricCard label="deliveries" value={overview?.outputJobs ?? '—'} />
                <MetricCard label="assists" value={fallbackCount} />
              </div>
            </CardContent>
          </Card>

          <Card className="epistemic-panel">
            <CardHeader>
              <CardTitle className="text-white">Resilience controls</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="epistemic-status-copy">
                (each hold keeps the surface available while the underlying path is withdrawn)
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" className="border-white/10 text-white/70 hover:text-synapse-gold" onClick={() => void handlePullback('arabic')}>
                  hold native meaning
                </Button>
                <Button variant="outline" className="border-white/10 text-white/70 hover:text-synapse-gold" onClick={() => void handlePullback('ouroboros')}>
                  hold guided path
                </Button>
                <Button variant="outline" className="border-white/10 text-white/70 hover:text-synapse-gold" onClick={() => void handlePullback('simd')}>
                  hold recall path
                </Button>
                <Button variant="outline" className="border-white/10 text-white/70 hover:text-synapse-gold" onClick={() => void handlePullback('axiom')}>
                  hold media path
                </Button>
              </div>
              <div className="space-y-2">
                {telemetry.slice(0, 4).map((entry) => (
                  <div key={`${entry.ts}-${entry.primary_attempted}`} className="rounded-2xl border border-white/8 bg-black/20 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ShieldAlert className="w-4 h-4 text-synapse-gold/75" />
                      <span className="epistemic-node-label">{translateTelemetryTitle(entry.primary_attempted)}</span>
                    </div>
                    <p className="epistemic-status-copy">{translateTelemetryReason(entry)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="space-y-2">
      <Label className="epistemic-node-label">{label}</Label>
      <Input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} className="epistemic-input" />
    </div>
  );
}

function ModeButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`epistemic-chip ${active ? 'is-active' : ''}`}
    >
      {children}
    </button>
  );
}

function MetricCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-black/20 p-4">
      <div className="epistemic-status-copy">({label})</div>
      <div className="mt-2 text-2xl font-semibold text-white">{value}</div>
    </div>
  );
}

function EpistemicTraceCanvas({ state }: { state: SurfaceState }) {
  const active = state === 'activated' || state === 'committed';
  const committed = state === 'committed';

  return (
    <div className="trace-canvas" aria-hidden="true">
      <span className="trace-node node-entry" data-active={active} />
      <span className="trace-node node-intent" data-active={active} />
      <span className="trace-node node-center" data-active={active || state === 'attentive'} />
      <span className="trace-node node-delivery" data-active={active || committed} />
      <span className="trace-node node-output" data-active={committed} />
      <span className="trace-node node-return" data-active={committed} />

      <span className="trace-line horizontal h1" data-active={active} />
      <span className="trace-line vertical v1" data-active={active} />
      <span className="trace-line horizontal h2" data-active={active || committed} />
      <span className="trace-line vertical v2" data-active={committed} />
      <span className="trace-line horizontal h3" data-active={committed} />
      <span className="trace-line horizontal h4 secondary" data-active={state === 'attentive'} />
      <span className="trace-line vertical v3 secondary" data-active={state === 'attentive'} />
      <span className="trace-line horizontal h5 tertiary" data-active={committed} />
    </div>
  );
}

function stateMicrocopy(state: SurfaceState): string {
  switch (state) {
    case 'dormant':
      return '(system potential without activation)';
    case 'activated':
      return '(selected path is carrying intent)';
    case 'committed':
      return '(decision recorded and held)';
    default:
      return '(ready without demand)';
  }
}

function stateCards(activeState: SurfaceState) {
  return [
    {
      title: 'Dormant',
      copy: '85% empty space. Quiet traces only.',
      active: activeState === 'dormant',
    },
    {
      title: 'Attentive',
      copy: 'A faint pulse suggests readiness.',
      active: activeState === 'attentive',
    },
    {
      title: 'Activated',
      copy: 'Only the chosen path brightens.',
      active: activeState === 'activated',
    },
    {
      title: 'Committed',
      copy: 'The path settles and stays dimly gold.',
      active: activeState === 'committed',
    },
  ];
}

function responseSteps(brainResult: BrainResult | null, outputJob: OutputJob | null): string[] {
  const steps = [
    'The surface accepted the current selection and kept the surrounding field quiet.',
    brainResult?.fallback_used
      ? 'An assist path absorbed the request without changing the visible flow.'
      : 'The request stayed on its preferred path without revealing which path was used.',
    outputJob
      ? outputJob.status === 'complete'
        ? 'Delivery finished and the selected path stabilized into a committed state.'
        : 'Delivery is being prepared behind the same surface language.'
      : 'Delivery remains optional until you ask for it.',
  ];

  return steps;
}

function translateMeaningMode(result: BrainResult | null): string {
  if (!result) return '(meaning awaiting)';
  switch (result.semantic_resolution.source) {
    case 'arabic':
      return '(native meaning)';
    case 'hybrid':
      return '(hybrid meaning)';
    default:
      return '(assisted meaning)';
  }
}

function translateTelemetryTitle(primaryAttempted: string): string {
  switch (primaryAttempted) {
    case 'arabic_semantics':
      return 'native meaning';
    case 'ouroboros':
      return 'guided path';
    case 'simd':
      return 'recall path';
    case 'axiom':
      return 'media path';
    default:
      return 'assist path';
  }
}

function translateTelemetryReason(entry: TelemetryEntry): string {
  if (!entry.fallback_triggered) {
    return '(the preferred path held without visible change)';
  }
  return '(the request was absorbed by a compatible backup path with no visible interruption)';
}
